import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding, useProperty } from '@vibesites/runtime/react';
import pagesData from '../pages.json';

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Same utility / funnel / template pages the Header keeps out of the top
 * nav. Sitemap mirrors nav visibility so toggling a page off in page
 * settings (or these hard exclusions) drops it from both places.
 */
const NAV_EXCLUDED_ALIASES = new Set([
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

export function SitemapContent() {
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', {
    collection: 'virtual_tours',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Match Header: Interactive Map only when both ApartmentSync IDs exist.
  const property = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = property?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = property?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;
  const hasInteractiveLeasing =
    !!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
    !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim());

  const links = useMemo(
    () =>
      Object.entries(pagesData as Record<string, PageEntry>)
        .filter(([alias, page]) => {
          if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
          // Home is `visible: false` so it stays out of the header nav
          // (logo covers that), but sitemaps should still list it.
          if (page.visible === false && alias !== 'home') return false;
          if (page.template) return false;
          if (page.path.includes('{{')) return false;
          if (alias === 'virtual-tour' && !hasVirtualTours) return false;
          if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
          return true;
        })
        .sort(([, a], [, b]) => a.position - b.position)
        .map(([, page]) => ({ name: page.name, path: page.path })),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  return (
    <section className="bg-[var(--vibe-color-background)]">
      <div className="border-t-4 border-b-4 border-[var(--vibe-color-text)]">
        <nav
          aria-label="Sitemap"
          className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 flex flex-col items-center gap-8 md:gap-10"
        >
          {links.map((link) => (
            <a
              key={link.path}
              href={link.path}
              className="font-body text-lg md:text-xl text-[var(--vibe-color-text)] text-center no-underline hover:opacity-70 transition-opacity"
            >
              {link.name}
            </a>
          ))}
        </nav>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {},
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(SitemapContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default SitemapContent;
